import { cn } from '../../../../../lib/utils';
import { secondaryText } from '../../../../../styling/stylingHelper';
import type { AICheckerTrace } from '../../../../../services/admin/adminService';
import type { ReviewBucket, ReviewItem } from '../../AuditReview';
import { MsgIdCopyCell } from '../shared/copyCells';
import { buildQuantityBuckets } from '../quantityAudit/quantityBuckets';
import { AICheckerEmailBody } from './AICheckerEmailBody';
import {
  DeepCheckButton,
  DeleteOrderButton,
  EmailIssuePromptCopyCell,
  ReauditButton,
  ReprocessButton,
} from './rowButtons';
import type { TFn } from '../types';

// One email's full detail card (shared by the review walk-through and the single-issue modal).
// onChanged fires after a mutating action (reprocess/reaudit/delete) so the caller can refresh.
export function aiCheckerEmailItem(trace: AICheckerTrace, t: TFn, onChanged?: () => void): ReviewItem {
  return {
    id: trace.id,
    title: trace.emailFrom || trace.platform || '—',
    subtitle: trace.restaurantName ?? trace.emailSubject ?? undefined,
    body: <AICheckerEmailBody trace={trace} t={t} />,
    actions: (
      <>
        <EmailIssuePromptCopyCell trace={trace} t={t} />
        <ReauditButton trace={trace} t={t} onChanged={onChanged} />
        <DeepCheckButton trace={trace} t={t} />
        <ReprocessButton trace={trace} t={t} onChanged={onChanged} />
        <DeleteOrderButton trace={trace} t={t} onChanged={onChanged} />
        <span className={cn(secondaryText, 'text-xs ml-1')}>Gmail:</span>
        <MsgIdCopyCell rfc822MessageId={trace.rfc822MessageId} label={trace.rfc822MessageId || trace.gmailMessageId} />
      </>
    ),
  };
}

export function buildAICheckerBuckets(issues: AICheckerTrace[], t: TFn, onChanged?: () => void): ReviewBucket[] {
  const buckets: ReviewBucket[] = [];

  const emailItem = (trace: AICheckerTrace) => aiCheckerEmailItem(trace, t, onChanged);

  const newSuppliers = issues.filter(i => i.triageCategory === 'new_supplier');
  if (newSuppliers.length > 0) {
    buckets.push({ id: 'new', title: t('adminTesting.review.bucketNewSuppliers'), hint: t('adminTesting.review.bucketNewSuppliersHint'), items: newSuppliers.map(emailItem) });
  }
  const shouldProcess = issues.filter(i => i.triageCategory === 'needs_attention');
  if (shouldProcess.length > 0) {
    buckets.push({ id: 'attention', title: t('adminTesting.review.bucketNeedsAttention'), hint: t('adminTesting.review.bucketNeedsAttentionHint'), items: shouldProcess.map(emailItem) });
  }
  const wrong = issues.filter(i => i.emailVerdict === 'wrong');
  if (wrong.length > 0) {
    buckets.push({ id: 'wrong', title: t('adminTesting.review.bucketWrongTotals'), hint: t('adminTesting.review.bucketWrongTotalsHint'), items: wrong.map(emailItem) });
  }
  const revenueIssues = issues.filter(i => i.matchedIn === 'revenue' && i.hasIssue);
  if (revenueIssues.length > 0) {
    buckets.push({ id: 'revenue-issue', title: t('adminTesting.review.bucketRevenueIssue'), hint: t('adminTesting.review.bucketRevenueIssueHint'), items: revenueIssues.map(emailItem) });
  }
  // Manually marked emails the checker itself found nothing wrong with (a safe skip, a clean order):
  // without their own bucket they'd count towards the score but appear in no walkthrough.
  const bucketed = new Set([...newSuppliers, ...shouldProcess, ...wrong, ...revenueIssues].map(i => i.id));
  const manual = issues.filter(i => i.manualIssue && !bucketed.has(i.id));
  if (manual.length > 0) {
    buckets.push({ id: 'manual', title: t('adminTesting.review.bucketManualIssue'), hint: t('adminTesting.review.bucketManualIssueHint'), items: manual.map(emailItem) });
    for (const i of manual) bucketed.add(i.id);
  }

  // Quantity flags from processed orders → one bucket per supplier (reuses the quantity card). Only
  // OPEN flags (not marked done, not filed as a known to-do) are work items.
  const hasOpenQtyFlag = (i: AICheckerTrace) => i.quantityFlags.some(f => !f.resolved && !f.known);
  const qtyFlags = issues.flatMap(i => i.quantityFlags.filter(f => !f.resolved && !f.known).map(f => ({ ...f, restaurantName: i.restaurantName ?? undefined })));

  // Catch-all: every remaining open issue that none of the buckets above claimed — today that is an
  // order email with an 'inconclusive' verdict or a factuur that was expected but never created.
  // Deliberately a residual (`!bucketed`) rather than a list of those two conditions, so a NEW issue
  // shape can never again be counted in the hero yet be absent from the walkthrough: the two must
  // reconcile. Rows whose only open work is a quantity flag are excluded — they are the buckets below.
  const other = issues.filter(i => !bucketed.has(i.id) && !hasOpenQtyFlag(i));
  if (other.length > 0) {
    buckets.push({ id: 'other', title: t('adminTesting.review.bucketOther'), hint: t('adminTesting.review.bucketOtherHint'), items: other.map(emailItem) });
  }

  buckets.push(...buildQuantityBuckets(qtyFlags, t));

  return buckets;
}
